import React from 'react';
import { Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';
import { colors } from '../../styles/colors';
import { ButtonVariant } from '../../styles/buttons';
import { FontWeight, TextVariant } from '../../styles/typography';
import { fixedBottom } from '../../styles/fixedBottom';
import ArrowDownIcon from '../../icons/ArrowDownIcon';
import Button from '../Button/Button';
import { StatusBarViewModel } from './types/StatusBarViewModel';
import { ElectionType } from '../../api/api-types';
import { SchemaUserDeclaration } from '../../api/api-schema';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import GuardedDialogAction, {
  OnUnavailableAction,
} from './GuardedDialogAction';

const tabletHeaderQuery = '@media (min-width: 48em)';
const compactTabletHeaderQuery = '@media (min-width: 40em)';
const desktopHeaderQuery = '@media (min-width: 80em)';

function getDeclarationActionLabel(
  label: string | undefined,
  declarationCount: number,
  singularLabel: string,
  pluralLabel: string,
) {
  if (label) {
    if (declarationCount > 1) {
      return label.replace(/vælgererklæring/g, 'vælgererklæringer');
    }

    return label;
  }

  return declarationCount > 1 ? pluralLabel : singularLabel;
}

function DrawerToggleIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <span
      aria-hidden="true"
      sx={{
        display: 'block',
        width: rem(16),
        height: rem(16),
        color: colors.base.Text.Brand,
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 250ms ease',
        '@media (prefers-reduced-motion: reduce)': {
          transitionDuration: '0ms',
        },
      }}
    >
      <ArrowDownIcon style={{ display: 'block' }} />
    </span>
  );
}

function DeclarationTitle({
  partyName,
  title,
}: {
  partyName?: string | null;
  title: string;
}) {
  return (
    <Text
      as="span"
      data-declaration-drawer-title
      variant={TextVariant.HeaderSm}
      sx={{
        color: colors.base.Text.Primary,
        display: 'block',
        alignItems: 'baseline',
        fontWeight: FontWeight.Medium,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        [compactTabletHeaderQuery]: {
          display: 'flex',
        },
      }}
    >
      <span sx={{ flexShrink: 0 }}>{title}</span>
      {partyName && (
        <>
          <span aria-hidden="true" sx={{ flexShrink: 0, mx: rem(4) }}>
            /
          </span>
          <span
            sx={{
              display: 'block',
              fontFamily: 'body',
              fontWeight: FontWeight.Normal,
              color: colors.base.Text.Secondary,
              [compactTabletHeaderQuery]: {
                display: 'inline',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            {partyName}
          </span>
        </>
      )}
    </Text>
  );
}

function DeclarationDrawerHeader({
  isOpen,
  onToggle,
  partyName,
  title,
  confirmUrl,
  hasConfirmCta,
  statusBarModel,
  toggleButtonRef,
  confirmTargets,
  declarations,
  onUnavailableAction,
}: {
  isOpen: boolean;
  onToggle: () => void;
  partyName?: string | null;
  title: string;
  confirmUrl?: string;
  hasConfirmCta: boolean;
  statusBarModel?: StatusBarViewModel;
  toggleButtonRef?: React.Ref<HTMLButtonElement>;
  confirmTargets?: {
    electionType?: ElectionType;
    partyId?: string;
  }[];
  declarations?: SchemaUserDeclaration[];
  onUnavailableAction: OnUnavailableAction;
}) {
  return (
    <div
      sx={{
        backgroundColor: fixedBottom.declarationDrawerHeaderBackground,
        borderRadius: `${rem(20)} ${rem(20)} 0 0`,
        px: rem(20),
        pt: rem(20),
        pb: rem(24),
        position: 'relative',
        cursor: 'pointer',
        [tabletHeaderQuery]: {
          px: rem(40),
          py: rem(16),
        },
      }}
    >
      <button
        ref={hasConfirmCta ? toggleButtonRef : undefined}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-hidden={hasConfirmCta ? undefined : true}
        aria-label={isOpen ? 'Luk vælgererklæring' : 'Åbn vælgererklæring'}
        tabIndex={hasConfirmCta ? undefined : -1}
        sx={{
          appearance: 'none',
          border: 0,
          p: 0,
          background: 'transparent',
          cursor: 'pointer',
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      />

      <span
        aria-hidden="true"
        sx={{
          display: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          width: rem(24),
          height: rem(24),
          position: 'absolute',
          top: '50%',
          left: rem(40),
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          zIndex: 2,
          [desktopHeaderQuery]: {
            display: 'flex',
          },
        }}
      >
        <DrawerToggleIcon isOpen={isOpen} />
      </span>

      <div
        sx={{
          mx: 'auto',
          maxWidth: rem(952),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: rem(20),
          [tabletHeaderQuery]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: rem(24),
          },
          pointerEvents: 'none',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div
          sx={{
            p: 0,
            background: 'transparent',
            color: colors.base.Text.Primary,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: rem(20),
            minWidth: 0,
            textAlign: 'left',
            width: '100%',
            [compactTabletHeaderQuery]: {
              display: 'none',
            },
            [tabletHeaderQuery]: {
              display: 'none',
            },
          }}
        >
          <span sx={{ display: ['block', 'block', 'none'], minWidth: 0 }}>
            <DeclarationTitle partyName={partyName} title={title} />
          </span>
          <span
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: rem(24),
              height: rem(24),
              mt: rem(4),
            }}
          >
            <DrawerToggleIcon isOpen={isOpen} />
          </span>
        </div>

        <div
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: rem(20),
            [compactTabletHeaderQuery]: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: rem(24),
            },
          }}
        >
          <span
            sx={{
              display: 'none',
              minWidth: 0,
              flex: '1 1 auto',
              [compactTabletHeaderQuery]: {
                display: 'block',
              },
            }}
          >
            <DeclarationTitle partyName={partyName} title={title} />
          </span>

          {hasConfirmCta && (
            <GuardedDialogAction
              action={DeclarationActionTypes.confirm}
              href={confirmUrl}
              onUnavailableAction={onUnavailableAction}
              targets={confirmTargets ?? []}
              userDeclarations={declarations}
            >
              {({ onClick }) => (
                <Button
                  variant={ButtonVariant.Quarternary}
                  href={confirmUrl}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick(event);
                  }}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    flex: '0 1 auto',
                    px: [rem(32)],
                    pointerEvents: 'auto',
                    [compactTabletHeaderQuery]: {
                      width: 'auto',
                      minWidth: rem(242),
                    },
                  }}
                >
                  {getDeclarationActionLabel(
                    statusBarModel?.confirmDeclarationLabel,
                    confirmTargets?.length ?? 0,
                    'Bekræft vælgererklæring',
                    'Bekræft vælgererklæringer',
                  )}
                </Button>
              )}
            </GuardedDialogAction>
          )}

          {!hasConfirmCta && (
            <Button
              buttonRef={toggleButtonRef}
              variant={ButtonVariant.Quarternary}
              aria-expanded={isOpen}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
              sx={{
                width: '100%',
                minWidth: 0,
                flex: '0 1 auto',
                px: [rem(32)],
                pointerEvents: 'auto',
                backgroundColor:
                  colors.components.fixedBottom.DeclarationDrawer.ViewButton
                    .Background.Default,
                '&:hover': {
                  backgroundColor:
                    colors.components.fixedBottom.DeclarationDrawer.ViewButton
                      .Background.Hover,
                },
                [compactTabletHeaderQuery]: {
                  width: 'auto',
                  minWidth: rem(222),
                },
              }}
            >
              {getDeclarationActionLabel(
                statusBarModel?.viewDeclarationLabel,
                declarations?.length ?? 0,
                'Se vælgererklæring',
                'Se vælgererklæringer',
              )}
            </Button>
          )}

          <span
            aria-hidden="true"
            sx={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: rem(24),
              height: rem(24),
              [compactTabletHeaderQuery]: {
                display: 'flex',
              },
              [desktopHeaderQuery]: {
                display: 'none',
              },
            }}
          >
            <DrawerToggleIcon isOpen={isOpen} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default DeclarationDrawerHeader;
